import {IApiEndpoint, IOperationResponse, IPagination,} from "@localization/siteMap/siteMapInterfaces.ts";
import type {IAprovaOuReprovaOrcamentoDto} from "@models/orcamento/aprovacaoDto.js";
import {IRecursoTransferencia} from '@models/recurso/IRecursoTransferencia.ts';
import {ISolicitacaoTipoFiltroDto, ISolicitacaoTipoNovaDto,} from "@models/solicitacoes/dto.ts";
import {siteMapApi} from "@localization/siteMap/siteMap.ts";
import {ISolicitacaoOrcamento, StatusAprovacaoType} from "@models/solicitacoes/orcamento.js";
import type {IOrcamentoFiltroDto, IOrcamentoNovoDto} from "@models/solicitacoes/orcamentoDto.js";
import {
	ISolicitacao,
	ISolicitacaoFiltroDto,
	ISolicitacaoItem,
	ISolicitacaoTipo,
} from "@models/solicitacoes/solicitacoes.ts";
import {ISolicitacaoParametro} from "@models/solicitacoes/solicitacaoParametro.ts";
import {IItensSolicitacaoFilters} from "@models/solicitacoes/solicitacaoItemDto.ts";
import {ISolicitacaoQuickFix} from "@models/solicitacoes/ISolicitacaoQuickFix.ts";

export const _SolicitacoesEndpoints = {
	getSolicitacaoTipos: (filters: ISolicitacaoTipoFiltroDto, pagination: IPagination): IApiEndpoint => ({
		url: '/api/Solicitacao/GetSolicitacaoTipo',
		pagination: pagination,
		query: filters,
		method: "GET",
	}),
	novaSolicitacaoTipo: (body: ISolicitacaoTipoNovaDto): IApiEndpoint => ({
		url: '/api/Solicitacao/NovoTipoSolicitacao',
		body: body,
		method: 'POST',
	}),
	createSolicitacao: (body: ISolicitacao): IApiEndpoint<ISolicitacao> => ({
		url: '/api/Solicitacao/CreateSolicitacao',
		body: body,
		method: 'POST',
	}),
	getSolicitacaoTiposPesquisa: (search: string, pagination: IPagination): IApiEndpoint => ({
		url: '/api/Solicitacao/GetSolicitacaoTipoPesquisa',
		pagination: pagination,
		query: {
			search: search,
		},
		method: "GET",
	}),
	GetSolicitacoes: (filters: ISolicitacaoFiltroDto, pagination: IPagination): IApiEndpoint<ISolicitacao[]> => {
		return ({
			url: '/api/Solicitacao/GetSolicitacoes',
			pagination: pagination,
			query: {
				...filters,
				finalizado: filters.finalizado != "unset" ? filters.finalizado : undefined
			} as ISolicitacaoFiltroDto,
			method: "GET",
		})
	},
	GetOrcamentos: (filters: IOrcamentoFiltroDto, pagination: IPagination): IApiEndpoint<ISolicitacaoOrcamento[]> => {
		return ({
			url: '/api/Orcamentos/GetOrcamentos',
			pagination: pagination,
			query: {
				...filters,
				flagsInativas: filters.flags?.filter(x => x.valor == false).map(x => x.id),
				flagsAtivas: filters.flags?.filter(x => x.valor == true).map(x => x.id),
				flagsIndiferente: filters?.flags?.filter(x => x.valor == null).map(x => x.id),
				flags: undefined,
			} as IOrcamentoFiltroDto,
			method: "GET",
		})
	},
	CreateOrcamento: (body: IOrcamentoNovoDto): IApiEndpoint => ({
		url: '/api/Orcamentos/NovoOrcamento',
		body: body,
		method: 'PUT',
	}),
	AprovarOuReprovarOrcamento: (body: IAprovaOuReprovaOrcamentoDto): IApiEndpoint<ISolicitacaoOrcamento> => ({
		url: '/api/Orcamentos/AprovaOuReprovaOrcamento',
		body: body,
		method: 'POST',
	}),
	GetOrcamentoAnexo: (id: number): IApiEndpoint<string> => ({
		url: `/api/Orcamentos/GetOrcamentoAnexo/${id}`,
		method: "GET",
	}),
	getParametrosSolicitacoes: (pagination: IPagination): IApiEndpoint => ({
		url: '/api/Solicitacao/GetSolicitacaoParametros',
		pagination: pagination,
		method: "GET",
	}),
	getItensSolicitacao: (filter: IItensSolicitacaoFilters, pagination: IPagination): IApiEndpoint<ISolicitacaoItem[]> => ({
		url: '/api/Solicitacao/GetItensSolicitacao',
		pagination: pagination,
		query: filter,
		method: "GET",
	}),
	finalizarOrcamento: (orcamento: ISolicitacaoOrcamento): IApiEndpoint<ISolicitacaoItem> => (
		{
			url: '/api/Solicitacao/FinalizarOrcamento',
			body: orcamento,
			method: "POST",
		}
	),
	UpdateSolicitacaoTipo: (s: ISolicitacaoTipo): IApiEndpoint<ISolicitacaoTipo> => ({
		url: '/api/Solicitacao/UpdateTipoSolicitacao',
		body: s,
		method: 'PATCH',
	}),
	CreateTransferencia: (r: IRecursoTransferencia): IApiEndpoint<IRecursoTransferencia> => ({
		url: '/api/Solicitacao/CreateTransferencia',
		body: r,
		method: 'POST',
	}),
	RealizaTransferencia: (r: IRecursoTransferencia): IApiEndpoint<IRecursoTransferencia> => ({
		url: '/api/Solicitacao/RealizaTransferencia',
		body: r,
		method: 'PATCH',
	}),
	UpdateSolicitacao: (s: ISolicitacao): IApiEndpoint<ISolicitacao> => ({
		url: '/api/Solicitacao/UpdateSolicitacao',
		body: s,
		method: 'PATCH',
	}),
	FinalizaSolicitacao: (s: number): IApiEndpoint<ISolicitacao> => ({
		url: `/api/Solicitacao/FinalizarSolicitacao`,
		path: [s],
		method: 'POST',
	}),
	SetStatusItemSolicitacao: ({idItem, status}: { idItem: number, status: StatusAprovacaoType }) => ({
		url: `/api/Solicitacao/SetStatusItemSolicitacao/${idItem}`,
		body: {status},
		method: 'POST',
	}),
	GetMovimentacoesBySolicitacao: (solicitacaoId: number, pagination: IPagination): IApiEndpoint => ({
		url: `/api/Solicitacao/GetMovimentacoesBySolicitacao/${solicitacaoId}`,
		pagination: pagination,
		method: 'GET',
	}),
	CreateQuickFixRequest: (quickFix: ISolicitacaoQuickFix): IApiEndpoint<ISolicitacaoQuickFix> => ({
		url: '/api/Solicitacao/CreateQuickFixRequest',
		body: quickFix,
		method: 'POST',
	}),
	ApproveQuickFix: ({quickFixId}: { quickFixId: number }): IApiEndpoint<ISolicitacaoQuickFix> => ({
		url: `/api/Solicitacao/ApproveQuickFix/${quickFixId}`,
		method: 'POST',
	}),
	RejectQuickFix: ({quickFixId}: { quickFixId: number }): IApiEndpoint<ISolicitacaoQuickFix> => ({
		url: `/api/Solicitacao/RejectQuickFix/${quickFixId}`,
		method: 'POST'
	}),
	GetQuickFixes: ({solicitacaoId, status}: {
		solicitacaoId?: number[],
		status?: StatusAprovacaoType
	}, pagination: IPagination): IApiEndpoint<ISolicitacaoQuickFix[]> => ({
		url: '/api/Solicitacao/GetQuickFixes',
		query: {
			solicitacaoId: solicitacaoId,
			status: status,
		},
		pagination,
		method: 'GET'
	})
};

export const GetSolicitacaoTipos = async (dados: ISolicitacaoTipoFiltroDto, pagination: IPagination): Promise<IOperationResponse<ISolicitacaoTipo[]>> => {
	const endpoint = _SolicitacoesEndpoints.getSolicitacaoTipos(dados, pagination);

	return await siteMapApi.fetch(endpoint);
};


export const CriaSolicitacaoTipo = async (dados: ISolicitacaoTipoNovaDto): Promise<IOperationResponse<ISolicitacaoTipo>> => {
	const endpoint = _SolicitacoesEndpoints.novaSolicitacaoTipo(dados);

	return await siteMapApi.fetch(endpoint);
};

export const GetSolicitacoes = async (dados: ISolicitacaoFiltroDto, pagination: IPagination) => {
	const endpoint = _SolicitacoesEndpoints.GetSolicitacoes(dados, pagination);

	return await siteMapApi.request<ISolicitacao[]>(endpoint);
};

export const GetOrcamentosV2 = (dados: IOrcamentoFiltroDto, pagination: IPagination) => {
	return _SolicitacoesEndpoints.GetOrcamentos(dados, pagination);
};



export const GetParametrosSolicitacoes = async (pagination: IPagination): Promise<IOperationResponse<ISolicitacaoParametro[]>> => {
	const endpoint = _SolicitacoesEndpoints.getParametrosSolicitacoes(pagination);

	return await siteMapApi.request<ISolicitacaoParametro[]>(endpoint);
};

export const GetItensSolicitacao = (filters: IItensSolicitacaoFilters, pagination: IPagination) => {
	return _SolicitacoesEndpoints.getItensSolicitacao(filters, pagination);
};

